<script setup lang="ts">
/**
 * Confirm-before-write card for editor tools the agent parks for explicit human
 * approval — today only `resource_persist` (save the in-progress report/assessment
 * draft the server holds for this conversation). Renders the tool title + the
 * agent's change summary with Approve / Reject actions; once the user acts it goes
 * read-only and shows the resolved state. The trusted write happens server-side on
 * approval (the draft is never sent from the browser) — see `confirmResourceEdit`
 * in `useChat`.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ToolUiPart } from "../../types/chat";

const props = withDefaults(
  defineProps<{
    tool: ToolUiPart;
    /** Human title for the card (resolved from the manifest by the dispatcher). */
    title?: string;
    /** Disable actions (e.g. while a reply is streaming). */
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{
  (
    e: "confirm",
    payload: { invocationId: string; toolName: string; approve: boolean; summary?: string },
  ): void;
}>();

const { t } = useI18n({ useScope: "local" });

/**
 * Two confirm-before-write tools render through this card: `resource_persist`
 * (save a draft) and `run_automation` (start an automation). The verbs/badges
 * differ, so labels are namespaced by tool and selected here.
 */
const isRun = computed(() => props.tool.name === "run_automation");
const ns = computed(() => (isRun.value ? "run" : "resource"));

const heading = computed(() => props.title || t(`${ns.value}.heading`));
const iconName = computed(() =>
  isRun.value ? "heroicons:play-circle" : "heroicons:document-check",
);
const failureText = computed(() => t(`${ns.value}.failure`));
const approveLabel = computed(() => t(`${ns.value}.approve`));
const savedBadge = computed(() => t(`${ns.value}.badgeDone`));
const notDoneBadge = computed(() => t(`${ns.value}.badgeNotDone`));

/** The agent's short description of what it is about to write. */
const summary = computed(() => {
  const input = props.tool.input;
  if (input && typeof input === "object" && typeof (input as Record<string, unknown>).summary === "string") {
    return (input as Record<string, unknown>).summary as string;
  }
  return "";
});

const awaiting = computed(() => props.tool.state === "approval-requested");
const approved = computed(() => props.tool.state === "output-available");
const declined = computed(() => props.tool.state === "output-denied");
const failed = computed(() => props.tool.state === "output-error");

// Actions are driven purely by the (server-authoritative) tool state: once the
// user responds, `confirmResourceEdit` optimistically flips the state so the
// buttons hide; if the request fails it rolls the state back to
// `approval-requested`, which re-exposes the buttons for a retry (no local
// "acted" latch that could trap the user).
const readOnly = computed(() => props.disabled || !awaiting.value);

function respond(approve: boolean) {
  if (readOnly.value) {
    return;
  }
  emit("confirm", {
    invocationId: props.tool.invocationId,
    toolName: props.tool.name,
    approve,
    summary: summary.value || undefined,
  });
}
</script>

<template>
  <div
    class="not-prose space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4"
    data-slot="chat-confirm-card"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="flex min-w-0 items-center gap-2">
        <Icon :name="iconName" mode="svg" class="size-4 shrink-0 text-primary" />
        <span class="truncate text-sm font-medium text-foreground">{{ heading }}</span>
      </span>
      <Badge v-if="approved" variant="default" class="shrink-0">{{ savedBadge }}</Badge>
      <Badge v-else-if="failed" variant="destructive" class="shrink-0">{{ notDoneBadge }}</Badge>
      <Badge v-else-if="declined" variant="destructive" class="shrink-0">{{ t('badge.discarded') }}</Badge>
      <Badge v-else variant="outline" class="shrink-0">{{ t("badge.needsApproval") }}</Badge>
    </div>

    <p v-if="summary" class="text-sm text-muted-foreground">{{ summary }}</p>

    <p v-if="failed" class="text-xs text-destructive">
      {{ failureText }}
    </p>

    <div v-if="awaiting" class="flex justify-end gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        :disabled="disabled"
        @click="respond(false)"
      >
        {{ t("reject") }}
      </Button>
      <Button type="button" size="sm" :disabled="disabled" @click="respond(true)">
        {{ approveLabel }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  reject: Reject
  badge:
    discarded: Discarded
    needsApproval: Needs approval
  resource:
    heading: Save changes
    failure: The save could not be completed — the resource may have changed. Ask the agent to re-read it and try again.
    approve: Approve & save
    badgeDone: Saved
    badgeNotDone: Not saved
  run:
    heading: Run automation
    failure: The automation run could not be started. Check the automation and try again.
    approve: Approve & run
    badgeDone: Started
    badgeNotDone: Not started
</i18n>
